// @ts-nocheck
/**
 * Ugreen 站点专用：用户上下文 + data-track-event 业务 handlers。
 * 依赖先加载 GA4Track.core.js（window.AnalyticsTrackCore）。
 * 输出：window.UgreenGA4（与拆分前一致）
 */
(function (global) {
  'use strict';

  const Core = global.AnalyticsTrackCore;
  if (!Core || typeof Core.createClient !== 'function') {
    console.error(
      '[GA4Track.ugreen] AnalyticsTrackCore 未加载，请先引入 GA4Track.core.js',
    );
    return;
  }

  const { detectDevice } = Core;

  /** 仅包含有意义的值：跳过 null、undefined、空串或纯空白字符串 */
  function pickNonEmpty(params) {
    const out = {};
    for (const [key, value] of Object.entries(params)) {
      if (value == null) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      out[key] = typeof value === 'string' ? value.trim() : value;
    }
    return out;
  }

  function isMobileViewport() {
    return global.innerWidth <= 768;
  }

  function isUgreenHostname(hostname) {
    const h = String(hostname).toLowerCase();
    return h === 'ugreen.com' || h === 'www.ugreen.com';
  }

  /**
   * 站内：相对路径、#/查询串、或 ugreen.com / www.ugreen.com；其它子域视为站外。
   */
  function isInternalUgreenHref(href) {
    if (href == null) return true;
    const s = String(href).trim();
    // 虽然算是本站链接，但是还是返回 false，为了不走 delay_report 的逻辑
    if (s === '' || s.startsWith('#') || s.startsWith('?')) return false;

    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(s)) {
      try {
        const u = new URL(s);
        if (u.protocol === 'http:' || u.protocol === 'https:') {
          return isUgreenHostname(u.hostname);
        }
        return false;
      } catch {
        return true;
      }
    }

    if (s.startsWith('//')) {
      try {
        const u = new URL(`${global.location.protocol}${s}`);
        return isUgreenHostname(u.hostname);
      } catch {
        return false;
      }
    }

    return true;
  }

  /** Mega menu 产品点击：桌面端三级导航名，移动端 drawer 内拼接 */
  function buildTopNavProductName(navEl) {
    if (isMobileViewport()) {
      const root = navEl.closest('.menu-drawer__submenu');
      if (!root) return '';
      const n1 =
        root
          .querySelector('.menu-drawer__menu-item-text')
          ?.textContent?.trim() ?? '';
      const n2 =
        root
          .querySelector(
            '.ug-mega-menu-left-category-list-content-title_mobile',
          )
          ?.textContent?.trim() ?? '';
      const n3 =
        navEl
          .querySelector('.ug-mega-menu-product-item__title')
          ?.textContent?.trim() ?? 'View All';
      return `${n1}_${n2}_${n3}`;
    }
    const root = navEl.closest('.ug-mega-menu');
    if (!root) return '';
    const n1 =
      root.querySelector('.menu-list__link-title')?.textContent?.trim() ?? '';
    const n2 =
      root
        .querySelector('.ug-mega-menu-left-category-name.active')
        ?.textContent?.trim() ||
      root
        .querySelector('.ug-mega-menu-left-category-name:first-of-type')
        ?.textContent?.trim() ||
      '';
    const n3 =
      navEl
        .querySelector('.ug-mega-menu-product-item__title')
        ?.textContent?.trim() || 'View All';
    return `${n1}_${n2}_${n3}`;
  }

  function getUgreenUserContext() {
    const login_status = global.UGrequest?.CUSTOMER_ID ? 'Logged' : 'Logout';
    const user_id = global.UGrequest?.CUSTOMER_ID || undefined;
    const country_code = 'Official Site';
    const title = document.title;
    const pathname = global.location.pathname;
    return {
      operating_platform: detectDevice(),
      login_status,
      user_id,
      pathname,
      country_code,
      content_group: title,
      page_title: title,
      page_location: global.location.href,
    };
  }

  function createPushSearchEvent(publish) {
    /**
     * 与 search handler 约定：一律使用 search_term / product_sku / href（与 Liquid JSON 字段一致）
     */
    return function pushSearchEvent({ search_term, product_sku, href, type }) {
      const isProductResult =
        type === 'product_click' ||
        (product_sku != null && String(product_sku).trim() !== '');
      if (isProductResult) {
        publish({
          use_delay_report: isInternalUgreenHref(href),
          log_name: '搜索结果产品点击',
          event_name: 'search',
          event_parameters: {
            type: 'Click Related Searches',
            search_term,
            product_sku,
            href: href || undefined,
          },
        });
      } else {
        publish({
          log_name: '搜索框输入事件',
          event_name: 'search',
          event_parameters: {
            type: 'search_input',
            search_term,
          },
        });
      }
    };
  }

  /**
   * @param {{ publish: (p: object) => void }} deps
   */
  function createUgreenHandlers(deps) {
    const { publish } = deps;
    const pushSearchEvent = createPushSearchEvent(publish);

    return {
      search(ctx) {
        const { element, params } = ctx;
        const raw =
          params.search_term ??
          (element instanceof HTMLInputElement ? element.value : '');
        const search_term = String(raw).trim();
        if (!search_term) return;
        const product_sku = params.product_sku;
        const href =
          params.href ??
          (element instanceof HTMLAnchorElement
            ? (element.getAttribute('href') ?? '')
            : '');
        pushSearchEvent({
          search_term,
          product_sku,
          href,
          type: params.type,
        });
      },
      top_navigation(ctx) {
        const { element, params } = ctx;
        const { navigation_name } = params;
        if (params.type === 'navigation_click') {
          const href = params.href ?? element.getAttribute('href');
          publish({
            use_delay_report: isInternalUgreenHref(href),
            log_name: '导航菜单点击事件',
            event_name: 'top_navigation',
            event_parameters: {
              navigation_name,
            },
          });
        } else if (params.type === 'product_click') {
          const { product_sku } = params;
          const href = params.href ?? element.getAttribute('href');
          publish({
            use_delay_report: isInternalUgreenHref(href),
            log_name: '导航产品点击事件',
            event_name: 'top_navigation',
            event_parameters: {
              product_sku,
              navigation_name: buildTopNavProductName(element),
            },
          });
        }
      },
      click_button(ctx) {
        const { params } = ctx;
        const { module, button_name, position, product_sku, href } = params;
        const event_parameters = pickNonEmpty({
          module,
          button_name,
          position,
          product_sku,
        });
        publish({
          use_delay_report: href ? isInternalUgreenHref(href) : false,
          log_name: `${module} 按钮点击事件`,
          event_name: 'click_button',
          event_parameters,
        });
      },
      store_click(ctx) {
        const { params } = ctx;
        const { button_name } = params;
        const event_parameters = pickNonEmpty({
          button_name,
        });
        publish({
          log_name: '店铺按钮跳转',
          event_name: 'store_click',
          event_parameters,
        });
      },
      bottom_navigation(ctx) {
        const { params } = ctx;
        const { navigation_name, href } = params;
        publish({
          use_delay_report: isInternalUgreenHref(href),
          log_name: '底部导航点击事件',
          event_name: 'bottom_navigation',
          event_parameters: pickNonEmpty({ navigation_name }),
        });
      },
      bottom_function(ctx) {
        const { element, params } = ctx;
        const { type, button_name, href, module } = params;
        if (type === 'country') {
          publish({
            use_delay_report: isInternalUgreenHref(href),
            log_name: '国家选择按钮点击事件',
            event_name: 'bottom_function',
            event_parameters: pickNonEmpty({ button_name, module }),
          });
        } else if (type === 'social') {
          const judgeButtonName = (hrefStr) => {
            const h = String(hrefStr || '').toLowerCase();
            if (!h) return '';
            if (h.includes('facebook.com')) return 'Facebook';
            if (h.includes('instagram.com')) return 'Instagram';
            if (h.includes('x.com') || h.includes('twitter.com')) return 'X';
            if (h.includes('youtube.com')) return 'YouTube';
            if (h.includes('tiktok.com')) return 'TikTok';
            return '';
          };
          publish({
            log_name: '底部社媒按钮点击事件',
            event_name: 'bottom_function',
            event_parameters: pickNonEmpty({
              button_name: judgeButtonName(href),
            }),
          });
        }
      },
      faq_click(ctx) {
        const { event, params } = ctx;
        const t = event.target;
        // 点击 <label for> 会先后产生两次 click：①点在标题/icon 冒泡到容器 ②浏览器再对关联的 radio 派发一次。
        // 只处理 input 上那一次，避免双次触发。
        if (
          !(t instanceof HTMLInputElement) ||
          !t.classList.contains('ug-faq-answer-input')
        ) {
          return;
        }
        // FAQ 脚本在同阶段先于 document 冒泡切换 class；此时仅「展开」为 true，收起不设点
        if (!t.classList.contains('ug-faq-expanded')) return;
        const { module, button_name } = params;
        const event_parameters = pickNonEmpty({
          module,
          button_name,
        });
        publish({
          log_name: 'FAQ 点击事件',
          event_name: 'faq_click',
          event_parameters,
        });
      },
    };
  }

  function specialEvent(sdk) {
    document
      .querySelectorAll(
        '.search-empty-state-keywords .search-empty-state-keyword',
      )
      .forEach((el) => {
        el.addEventListener('click', () => {
          let params = {};
          try {
            params = JSON.parse(
              el.getAttribute('data-track-event-parameters') || '{}',
            );
          } catch {
            params = {};
          }
          const { position } = params;
          const targetA = el.querySelector('a');
          const href = targetA?.getAttribute('href') ?? '';
          const button_name = targetA?.textContent?.trim() ?? '';
          const event_parameters = pickNonEmpty({
            module: 'Top Searches',
            button_name,
            position,
          });
          sdk.publish({
            use_delay_report: isInternalUgreenHref(href),
            log_name: '点击事件',
            event_name: 'click_button',
            event_parameters,
          });
        });
      });

    document.querySelectorAll('.ug-footer-protocol-list a').forEach((el) => {
      el.addEventListener('click', () => {
        const href = el.getAttribute('href') ?? '';
        const button_name = el.textContent?.trim() ?? '';
        if (!button_name) return;
        const event_parameters = pickNonEmpty({
          module: 'Bottom Function',
          button_name,
        });
        sdk.publish({
          use_delay_report: isInternalUgreenHref(href),
          log_name: '底部隐私协议按钮点击事件',
          event_name: 'bottom_function',
          event_parameters,
        });
      });
    });
    if (typeof zE === 'function') {
      zE?.('messenger:on', 'open', () => {
        sdk.publish({
          log_name: 'Float Live Chat Icon 点击事件',
          event_name: 'click_button',
          event_parameters: pickNonEmpty({
            module: 'Float Live Chat',
            button_name: 'Float Live Chat Icon',
          }),
        });
      });
    }

    document.querySelectorAll('.ug-contact-section-item-text a').forEach((el) => {
      el.addEventListener('click', () => {
        const href = el.getAttribute('href') ?? '';
        const button_name = el.textContent?.trim().replaceAll('@', '_') ?? '';
        const event_parameters = pickNonEmpty({
          module: 'Business Inquiries',
          button_name,
        });
        sdk.publish({
          use_delay_report: isInternalUgreenHref(href),
          log_name: 'Business Inquiries 按钮点击事件',
          event_name: 'click_button',
          event_parameters,
        });
      });
    });
  }

  const sdk = Core.createClient({
    getUserContext: getUgreenUserContext,
    createHandlers: createUgreenHandlers,
  });

  /** 首页 Top Slides：≥50% 可见且持续 ≥1s 记一次曝光（见 GA4Track.core — createModuleExposure） */
  function initIndexSlidesModuleExposure() {
    if (typeof Core.createModuleExposure !== 'function') return;
    try {
      const exposure = Core.createModuleExposure({
        selector: '[data-module-exposure]',
        onExpose({ moduleId, params }) {
          sdk.publish({
            log_name: `${params.module} 模块曝光`,
            event_name: params.exposure_event_name ? params.exposure_event_name : 'module_impression',
            event_parameters: pickNonEmpty({
              module: params.module,
              banner_name: params.banner_name,
              position: params.position,
              product_sku: params.product_sku,
            }),
          });
        },
      });
      exposure.start();
    } catch (err) {
      console.error(
        '[GA4Track.ugreen] initIndexSlidesModuleExposure failed:',
        err,
      );
    }
  }

  global.UgreenGA4 = sdk;
  sdk.init();
  specialEvent(sdk);
  initIndexSlidesModuleExposure();
})(typeof window !== 'undefined' ? window : this);
